import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import OrderService from '../../services/order/order.service';
import AuthService from "../../services/autentication/auth.service";

interface ProductData {
  expectedDeliveryDate?: string;
  requestedDate?: string;
}

interface OrderData {
  orderID: string;
  orderDate: string;
  products?: ProductData[];
  orderStatusName: string;
  invoices?: any[];
}

interface OrderReviewTopProps {
  orderData?: OrderData | null;
  hideEstimatedDelivery?: boolean;
}

const OrderReviewTop = ({ orderData, hideEstimatedDelivery = false }: OrderReviewTopProps) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);

  const [roleId, setRoleId] = useState<number>(0);
  const [requestedDate, setRequestedDate] = useState<Date | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const orderDate = orderData?.orderDate ? new Date(orderData.orderDate) : new Date();

  useEffect(() => {
    if (AuthService.isUserLoggedIn()) {
      const localData = localStorage.getItem("user");
      if (localData) {
        const parsed = JSON.parse(localData);
        setRoleId(parsed.roleID);
      }
    }
  }, []);

  // Load requested delivery date
  useEffect(() => {
    const requestedDateStr = orderData?.products?.[0]?.requestedDate;
    setRequestedDate(requestedDateStr ? new Date(requestedDateStr) : null);
  }, [orderData]);

  if (!orderData) return null;

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSaveRequestedDate = async () => {
    if (!requestedDate) return;

    try {
      await OrderService.setOrderDeliveryDate(orderData.orderID, formatDate(requestedDate));

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Requested delivery date updated",
      });

      setIsEditing(false);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update requested date",
      });
    }
  };

  const handleCancelRequested = () => {
    const originalDateStr = orderData?.products?.[0]?.requestedDate;
    setRequestedDate(originalDateStr ? new Date(originalDateStr) : null);
    setIsEditing(false);
  };

  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return "N/A";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

 const canEditDeliveryDate =
  roleId === 2 &&
  orderData?.orderStatusName !== "Cancelled" &&
  (orderData?.invoices?.length ?? 0) === 0;

  const estimatedDate = orderData?.products?.[0]?.expectedDeliveryDate
    ? new Date(orderData.products[0].expectedDeliveryDate)
    : null;

  const displayRequestedDate = requestedDate ? formatDisplayDate(requestedDate) : "No Requested Date";

  return (
    <div className="grid order-top-color mb-3 px-3">
      <div className="col-12 md:col-6">
        <b>Order #: {orderData.orderID}</b>
      </div>

      <div className="col-12 md:col-6 text-right left-mobile">
        <p className="mb-1 font-semibold">
          {t("ordersummary_order_date")} :{" "}
          {orderData.orderDate
            ? new Date(orderData.orderDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
            : "N/A"}
        </p>
        {/* ✅ Estimated Delivery - always NON editable */}
        {!hideEstimatedDelivery && (
          <p className="mb-1 font-semibold" style={{ whiteSpace: "nowrap", display: "flex", gap: "0.25rem", justifyContent: "flex-end" }}>
            Estimated Delivery Date (On or before):{" "}
            <span className="font-semibold">
              {formatDisplayDate(estimatedDate)}
            </span>
          </p>
        )}

        {/* ✅ Requested Delivery - Editable Based on Permission */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <p className="mb-1 font-semibold">Requested Delivery Date:</p>

          {canEditDeliveryDate ? (
            <>
              <Calendar
                value={requestedDate}
                onChange={(e) => {
                  setRequestedDate(e.value as Date | null);
                  setIsEditing(true);
                }}
                dateFormat="dd/mm/yy"
                showButtonBar={false}
                style={{ width: "auto", minWidth: "12rem", backgroundColor: "white" }}
                minDate={new Date()}
                className="w-full"
                readOnlyInput
              />

              {isEditing && (
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Button
                    icon="pi pi-check"
                    onClick={handleSaveRequestedDate}
                    className="p-button-sm"
                  />
                  <Button
                    icon="pi pi-times"
                    onClick={handleCancelRequested}
                    className="p-button-sm p-button-secondary"
                  />
                </div>
              )}
            </>
          ) : (
            <span className="font-semibold">{displayRequestedDate} </span>
          )}
        </div>

        <Toast ref={toast} />
      </div>
    </div>
  );
};

export default OrderReviewTop;
